import React, { useState, useEffect } from 'react';
import { toggleBuy, toggleSell, toggleP2P, toggleClaim, updateAdminConfig } from '../../utils/api';

/*
  Controls — Buy & Sell rules for the platform.

  Layout (top → bottom):
    1. Current Rules  — plain-language summary of what's live right now
    2. Live Switches  — pause/resume buy, sell, transfers, claims, P2P
    3. Buy Rules      — per-period buy cap (amount + months) + min buy
    4. Sell Rules     — withdraw multiplier (2x/3x) + burn % + min sell
    5. Exempt Wallets — wallets with no sell cap
    6. P2P Settings   — burn % + max multiplier

  Every value is dynamic (saved to SystemConfig via updateAdminConfig).
*/

const Controls = ({ config, loadConfig, flash, f, SYM }) => {
  const v = (val, def) => (val != null ? val : def);

  // ── Buy rules ──
  const [buyLimitMode, setBuyLimitMode] = useState(config.buyLimitMode || 'unlimited');
  const [maxBuy, setMaxBuy] = useState(String(v(config.maxBuyUsdt, 5000)));
  const [buyCapMonths, setBuyCapMonths] = useState(String(v(config.buyCapPeriodMonths, 6)));
  const [minBuy, setMinBuy] = useState(String(v(config.minBuyUsdt, 10)));
  const [minBuyBuffer, setMinBuyBuffer] = useState(String(v(config.minBuyBufferUsdt, 1)));

  // ── Sell rules ──
  const [withdrawX, setWithdrawX] = useState(String(v(config.maxWithdrawMultiplier, 2)));
  const [sellBurn, setSellBurn] = useState(String(v(config.sellFeePercent, 10)));
  const [minSellTokens, setMinSellTokens] = useState(String(v(config.minSellTokens, 1)));

  // ── Exempt wallets ──
  const [exemptText, setExemptText] = useState((config.sellExemptWallets || []).join('\n'));
  const [savingExempt, setSavingExempt] = useState(false);

  // ── P2P ──
  const [p2pBurn, setP2pBurn] = useState(String(v(config.p2pBurnPercent, 0)));
  const [p2pMax, setP2pMax] = useState(String(v(config.p2pMaxMultiplier, 2)));

  useEffect(() => {
    setBuyLimitMode(config.buyLimitMode || 'unlimited');
    setMaxBuy(String(v(config.maxBuyUsdt, 5000)));
    setBuyCapMonths(String(v(config.buyCapPeriodMonths, 6)));
    setMinBuy(String(v(config.minBuyUsdt, 10)));
    setMinBuyBuffer(String(v(config.minBuyBufferUsdt, 1)));
    setWithdrawX(String(v(config.maxWithdrawMultiplier, 2)));
    setSellBurn(String(v(config.sellFeePercent, 10)));
    setMinSellTokens(String(v(config.minSellTokens, 1)));
    setExemptText((config.sellExemptWallets || []).join('\n'));
    setP2pBurn(String(v(config.p2pBurnPercent, 0)));
    setP2pMax(String(v(config.p2pMaxMultiplier, 2)));
  }, [config]);

  const doToggle = async (fn, label) => {
    try { const r = await fn(); flash(r.message); loadConfig(); }
    catch { flash(`Failed to toggle ${label}`, 'error'); }
  };

  const saveBuy = async () => {
    try {
      await updateAdminConfig({
        buyLimitMode,
        maxBuyUsdt: buyLimitMode === 'unlimited' ? 0 : (parseFloat(maxBuy) || 0),
        buyCapPeriodMonths: parseInt(buyCapMonths) || 6,
        minBuyUsdt: parseFloat(minBuy) || 0,
        minBuyBufferUsdt: parseFloat(minBuyBuffer) || 0,
      });
      flash('Buy rules saved'); loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
  };

  const saveSell = async () => {
    try {
      await updateAdminConfig({
        maxWithdrawMultiplier: parseFloat(withdrawX) || 2,
        sellFeePercent: parseFloat(sellBurn) || 0,
        minSellTokens: parseFloat(minSellTokens) || 0,
      });
      flash('Sell rules saved'); loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
  };

  const saveExempt = async () => {
    setSavingExempt(true);
    try {
      const list = exemptText.split(/[\s,]+/).map(w => w.trim().toLowerCase()).filter(Boolean);
      await updateAdminConfig({ sellExemptWallets: list });
      flash(`Exempt wallets saved (${list.length})`); loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setSavingExempt(false);
  };

  const saveP2P = async () => {
    try {
      await updateAdminConfig({ p2pBurnPercent: parseFloat(p2pBurn) || 0, p2pMaxMultiplier: parseFloat(p2pMax) || 2 });
      flash('P2P settings saved'); loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
  };

  // ── Plain-language effective rules (read from SAVED config) ──
  const isLimited = config.buyLimitMode === 'limited' && config.maxBuyUsdt > 0;
  const savedBuyRule = isLimited
    ? `Up to ${f(config.maxBuyUsdt, 0)} USDT every ${config.buyCapPeriodMonths || 6} month(s) per user`
    : 'Unlimited — no per-user buy cap';
  const exemptCount = (config.sellExemptWallets || []).length;

  // ── Live preview (from the edit fields, updates as you type) ──
  const buyPreview = buyLimitMode === 'unlimited'
    ? 'Users can buy any amount, anytime.'
    : `Each user can buy up to ${maxBuy || 0} USDT (or crypto equivalent) within ${buyCapMonths || 6} month(s), counted from their first buy of the period. After that it resets to a fresh ${maxBuy || 0} USDT — unused amount does NOT carry over.`;
  const sellPreview = `Each user can withdraw up to ${withdrawX || 2}× their TOTAL invested — partially or fully, anytime (no time lock, no unlock gate). ${sellBurn || 0}% of tokens are burned on each sell.`;

  const SwitchCard = ({ label, isOn, onText, offText, onClick }) => (
    <button type="button" role="switch" aria-checked={isOn}
      className="ctl-switch" onClick={onClick}>
      <span className="ctl-switch-info">
        <span className="ctl-switch-label">{label}</span>
        <span className={`ctl-state ${isOn ? 'on' : 'off'}`}>{isOn ? onText : offText}</span>
      </span>
      <span className={`ctl-toggle ${isOn ? 'on' : ''}`}><span className="ctl-toggle-knob" /></span>
    </button>
  );

  return (
    <div className="fade-in ctl-root">
      <style>{`
        .ctl-root { width: 100%; box-sizing: border-box; }
        .ctl-root * { box-sizing: border-box; }
        .ctl-sub { font-weight: 400; opacity: .6; font-size: 12px; text-transform: none; letter-spacing: 0; }
        .ctl-section-title {
          margin: 28px 0 12px; font-size: 12.5px; font-weight: 700; text-transform: uppercase;
          letter-spacing: .07em; display: flex; align-items: center; gap: 10px; opacity: .95;
        }
        .ctl-section-title::before {
          content: ''; width: 4px; height: 15px; border-radius: 3px; background: var(--accent); flex-shrink: 0;
        }

        /* Summary */
        .ctl-summary {
          border: 1px solid var(--border); border-left: 4px solid var(--accent);
          padding: 18px 20px; border-radius: 14px;
          background: linear-gradient(135deg, var(--accent-dim, rgba(79,70,229,.08)), transparent 72%);
        }
        .ctl-summary-h { margin: 0 0 12px; font-size: 15px; }
        .ctl-rules { display: grid; gap: 2px; }
        .ctl-rule { display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap;
          padding: 9px 0; border-bottom: 1px dashed rgba(128,128,128,.22); font-size: 13.5px; }
        .ctl-rule:last-child { border-bottom: none; }
        .ctl-rule-k { opacity: .68; }
        .ctl-rule-v { font-weight: 600; text-align: right; }

        /* Switches */
        .ctl-switch-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(184px, 1fr)); gap: 12px; }
        .ctl-switch {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          width: 100%; text-align: left; cursor: pointer; padding: 15px 16px;
          background: var(--bg-sidebar); border: 1px solid var(--border); border-radius: 14px;
          transition: border-color .15s ease, box-shadow .15s ease, transform .12s ease;
        }
        .ctl-switch:hover { border-color: var(--accent); box-shadow: 0 6px 18px var(--shadow, rgba(0,0,0,.08)); transform: translateY(-1px); }
        .ctl-switch-info { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .ctl-switch-label { font-weight: 700; font-size: 14px; }
        .ctl-state { font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
        .ctl-state.on { color: #16a34a; }
        .ctl-state.off { color: #ef4444; }
        .ctl-toggle {
          position: relative; flex-shrink: 0; width: 46px; height: 26px; border-radius: 999px;
          background: rgba(128,128,128,.35); transition: background .2s ease;
        }
        .ctl-toggle.on { background: #16a34a; }
        .ctl-toggle-knob {
          position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 50%;
          background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.35); transition: transform .2s ease;
        }
        .ctl-toggle.on .ctl-toggle-knob { transform: translateX(20px); }

        /* Setting sections */
        .ctl-card { padding: 20px 22px; border-radius: 14px; }
        .ctl-card h3 { margin: 0 0 4px; font-size: 15px; }
        .ctl-card .ctl-card-desc { margin: 0 0 14px; font-size: 12.5px; opacity: .7; }
        .ctl-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; }
        .ctl-field label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; }
        .ctl-field .input { width: 100%; }
        .ctl-field .adm-hint, .ctl-field .ctl-hint { font-size: 11.5px; opacity: .65; margin-top: 5px; line-height: 1.45; }
        .ctl-preview { margin-top: 14px; padding: 11px 13px; border-radius: 10px;
          background: rgba(79,70,229,.09); border: 1px solid rgba(79,70,229,.18);
          font-size: 12.8px; line-height: 1.55; }
        .ctl-preview b { font-weight: 700; }
        .ctl-mt { margin-top: 16px; }
        .ctl-textarea { width: 100%; font-family: monospace; font-size: 13px; padding: 12px 14px;
          border-radius: 10px; resize: vertical; }
      `}</style>

      <h2 className="adm-title">Buy &amp; Sell Controls</h2>

      {/* ── 1. CURRENT RULES SUMMARY ── */}
      <div className="card ctl-summary">
        <h3 className="ctl-summary-h">Current Rules <span className="ctl-sub">— what applies to users right now</span></h3>
        <div className="ctl-rules">
          <div className="ctl-rule"><span className="ctl-rule-k">Buy limit</span><span className="ctl-rule-v">{savedBuyRule}</span></div>
          <div className="ctl-rule"><span className="ctl-rule-k">Sell limit</span><span className="ctl-rule-v">{config.maxWithdrawMultiplier || 2}× of total invested — anytime, no time lock</span></div>
          <div className="ctl-rule"><span className="ctl-rule-k">Sell burn</span><span className="ctl-rule-v">{config.sellFeePercent || 0}% burned per sell</span></div>
          <div className="ctl-rule"><span className="ctl-rule-k">Exempt wallets</span><span className="ctl-rule-v">{exemptCount} wallet(s) — no sell cap</span></div>
          <div className="ctl-rule"><span className="ctl-rule-k">Minimums</span><span className="ctl-rule-v">Buy {f(config.minBuyUsdt, 0)} USDT · Sell {f(config.minSellTokens, 0)} {SYM}</span></div>
          <div className="ctl-rule"><span className="ctl-rule-k">Status</span><span className="ctl-rule-v">
            Buy {config.isBuyPaused ? '⏸' : '▶'} · Sell {config.isSellPaused ? '⏸' : '▶'} · P2P {config.isP2PEnabled ? '▶' : '⏸'} · Claims {config.isClaimPaused ? '⏸' : '▶'}
          </span></div>
        </div>
      </div>

      {/* ── 2. LIVE SWITCHES ── */}
      <h3 className="ctl-section-title">Live Switches <span className="ctl-sub">— pause / resume instantly</span></h3>
      <div className="ctl-switch-grid">
        <SwitchCard label="Buy"       isOn={!config.isBuyPaused}   onText="Active"  offText="Paused"   onClick={() => doToggle(toggleBuy, 'buy')} />
        <SwitchCard label="Sell"      isOn={!config.isSellPaused}  onText="Active"  offText="Paused"   onClick={() => doToggle(toggleSell, 'sell')} />
        <SwitchCard label="Claims"    isOn={!config.isClaimPaused} onText="Active"  offText="Paused"   onClick={() => doToggle(toggleClaim, 'claim')} />
        <SwitchCard label="P2P"       isOn={!!config.isP2PEnabled} onText="Enabled" offText="Disabled" onClick={() => doToggle(toggleP2P, 'p2p')} />
      </div>

      {/* ── 3. BUY RULES ── */}
      <h3 className="ctl-section-title">Buy Rules</h3>
      <div className="card ctl-card">
        <h3>Purchase limit per user</h3>
        <p className="ctl-card-desc">Cap how much a single user can buy, and over what period.</p>
        <div className="ctl-form">
          <div className="ctl-field">
            <label>Buy Limit Mode</label>
            <select value={buyLimitMode} onChange={e => setBuyLimitMode(e.target.value)} className="input">
              <option value="unlimited">Unlimited — no cap</option>
              <option value="limited">Limited — cap per period</option>
            </select>
            <p className="ctl-hint">Choose <b>Limited</b> to enable the amount + period below.</p>
          </div>

          {buyLimitMode === 'limited' && (
            <div className="ctl-field">
              <label>Max Buy per Period (USDT equiv.)</label>
              <input type="number" value={maxBuy} onChange={e => setMaxBuy(e.target.value)} className="input" />
              <p className="ctl-hint">e.g. 5000 → a user can buy up to 5000 USDT (or crypto worth that) per period.</p>
            </div>
          )}

          {buyLimitMode === 'limited' && (
            <div className="ctl-field">
              <label>Cap Period (months)</label>
              <input type="number" value={buyCapMonths} onChange={e => setBuyCapMonths(e.target.value)} className="input" />
              <p className="ctl-hint">e.g. 6 → the cap resets every 6 months (from the user's first buy of the period).</p>
            </div>
          )}

          <div className="ctl-field">
            <label>Minimum Buy (USDT equiv.)</label>
            <input type="number" value={minBuy} onChange={e => setMinBuy(e.target.value)} className="input" />
            <p className="ctl-hint">Smallest allowed single purchase. This is what the user sees and must enter on the buy form.</p>
          </div>

          <div className="ctl-field">
            <label>Minimum Buy Buffer (USDT)</label>
            <input type="number" value={minBuyBuffer} onChange={e => setMinBuyBuffer(e.target.value)} className="input" />
            <p className="ctl-hint">Tolerance the backend allows below the minimum, so a small price change between quote and on-chain verification doesn't fail a valid order. e.g. Minimum 10 with buffer 1 → users still see 10, but the backend accepts down to <b>{(Math.max(0, (parseFloat(minBuy) || 0) - (parseFloat(minBuyBuffer) || 0))).toFixed(2)}</b> USDT.</p>
          </div>
        </div>
        <div className="ctl-preview"><b>Result:</b> {buyPreview}</div>
        <button className="btn btn-primary ctl-mt" onClick={saveBuy}>Save Buy Rules</button>
      </div>

      {/* ── 4. SELL RULES ── */}
      <h3 className="ctl-section-title">Sell Rules</h3>
      <div className="card ctl-card">
        <h3>Withdrawal limit &amp; sell burn</h3>
        <p className="ctl-card-desc">How much of their investment users can pull out, and the burn on each sell. No time lock.</p>
        <div className="ctl-form">
          <div className="ctl-field">
            <label>Withdraw Multiplier (× total invested)</label>
            <input type="number" value={withdrawX} onChange={e => setWithdrawX(e.target.value)} className="input" />
            <p className="ctl-hint">e.g. 2 → a user can withdraw up to 2× of everything they invested. Set 3 for 3×, etc.</p>
          </div>
          <div className="ctl-field">
            <label>Sell Burn % (tokens burned on sell)</label>
            <input type="number" value={sellBurn} onChange={e => setSellBurn(e.target.value)} className="input" />
            <p className="ctl-hint">{sellBurn || 0}% burned, user receives value of the remaining {100 - (parseFloat(sellBurn) || 0)}%.</p>
          </div>
          <div className="ctl-field">
            <label>Minimum Sell ({SYM})</label>
            <input type="number" value={minSellTokens} onChange={e => setMinSellTokens(e.target.value)} className="input" />
            <p className="ctl-hint">Smallest allowed single sell.</p>
          </div>
        </div>
        <div className="ctl-preview"><b>Result:</b> {sellPreview}</div>
        <button className="btn btn-primary ctl-mt" onClick={saveSell}>Save Sell Rules</button>
      </div>

      {/* ── 5. EXEMPT WALLETS ── */}
      <h3 className="ctl-section-title">Sell-Exempt Wallets</h3>
      <div className="card ctl-card">
        <h3>Wallets with no sell cap</h3>
        <p className="ctl-card-desc">
          These wallets ignore the {config.maxWithdrawMultiplier || 2}× cap completely — they can withdraw
          any amount, anytime (even if their portfolio is 4×+). One wallet address per line.
        </p>
        <textarea
          value={exemptText}
          onChange={e => setExemptText(e.target.value)}
          className="input ctl-textarea"
          rows={5}
          placeholder={'0xabc123...\n0xdef456...'}
        />
        <button className="btn btn-primary ctl-mt" onClick={saveExempt} disabled={savingExempt}>
          {savingExempt ? 'Saving...' : 'Save Exempt Wallets'}
        </button>
      </div>

      {/* ── 6. P2P ── */}
      <h3 className="ctl-section-title">P2P Transfer Settings</h3>
      <div className="card ctl-card">
        <h3>Peer-to-peer transfers</h3>
        <p className="ctl-card-desc">Applies when P2P is enabled (see Live Switches above).</p>
        <div className="ctl-form">
          <div className="ctl-field">
            <label>P2P Burn % (tokens burned on transfer)</label>
            <input type="number" value={p2pBurn} onChange={e => setP2pBurn(e.target.value)} className="input" />
            <p className="ctl-hint">{p2pBurn === '0' ? 'No burn — 100% transferred.' : `${p2pBurn}% burned, ${100 - (parseFloat(p2pBurn) || 0)}% received.`}</p>
          </div>
          <div className="ctl-field">
            <label>P2P Max (× deposit value)</label>
            <input type="number" value={p2pMax} onChange={e => setP2pMax(e.target.value)} className="input" />
            <p className="ctl-hint">Max P2P = {p2pMax || 0}× of the sender's deposit value.</p>
          </div>
        </div>
        <button className="btn btn-primary ctl-mt" onClick={saveP2P}>Save P2P Settings</button>
      </div>
    </div>
  );
};

export default Controls;
